import { usePurchases } from "@saas/payments/hooks/purchases";
import { useCallback, useEffect, useRef } from "react";

/** 先让用户看清成品再弹，弹窗不挡住刚出来的结果 */
const POST_GENERATION_PAYWALL_DELAY_MS = 3000;

/**
 * 没付过钱的用户（订阅、积分包都没有，同去水印闸门的 canRemoveWatermark）每次生成
 * 成功都弹一次付费墙，不等积分用完（2026-10-08 老公定）。
 *
 * 付没付过钱在弹出那一刻才判断，不在成功时判断：成功时购买记录可能还没加载完，
 * 这 3 秒里也可能刚付了钱。购买记录还在加载或请求失败时不弹，宁可漏弹一个
 * 免费用户，也不打扰付费用户。
 *
 * 返回的函数引用稳定：可以放进只依赖轮询结果的 effect 里，不会让 effect 多跑一遍。
 * openPaywall 每次渲染都取最新的，里面读到的弹窗状态是当前值。
 */
export function usePostGenerationPaywall(openPaywall: () => void) {
	const { canRemoveWatermark, loading, isError } = usePurchases();
	const latestRef = useRef({
		canRemoveWatermark,
		loading,
		isError,
		openPaywall,
	});
	latestRef.current = { canRemoveWatermark, loading, isError, openPaywall };
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(
		() => () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		},
		[],
	);

	return useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}
		timerRef.current = setTimeout(() => {
			timerRef.current = null;
			const { canRemoveWatermark, loading, isError, openPaywall } =
				latestRef.current;
			if (loading || isError || canRemoveWatermark) {
				return;
			}
			openPaywall();
		}, POST_GENERATION_PAYWALL_DELAY_MS);
	}, []);
}
